/**
 * scheduleSynth — deterministic agent that derives a project schedule
 * from the typed hypergraph.  Reads the :ProjectStage template from
 * codegen.json#projectStages (NOT hardcoded inline), reads the project's
 * handover date + room kinds + imported SKU lead-times, and emits:
 *
 *   - One typed :Milestone per applicable stage with `dueOn`
 *     (xsd:dateTime), `slackDays`, `synthesisActivityId`, status.
 *   - One :Synthesis prov:Activity node carrying prov:atTime.
 *   - DEPENDS_ON edges between stages.
 *   - WAS_GENERATED_BY edges from each Milestone → :Synthesis.
 *
 * No hardcoded stage labels in code. No hardcoded offsets. Every value
 * comes from the ontology JSON. CLAUDE.md §1 + §10 + §12. The Why-engine
 * walks the WAS_GENERATED_BY edges to answer "why is this milestone here?".
 */
import codegen from "../../../../docs/ontology/codegen.json";
import type { GraphEdge, GraphNode, ProjectGraph } from "../../types/ontology";

export interface SynthResult {
  readonly milestones: ReadonlyArray<GraphNode>;
  readonly dependsOn: ReadonlyArray<GraphEdge>;
  readonly wasGeneratedBy: ReadonlyArray<GraphEdge>;
  readonly activity: GraphNode;
  /** Stage ids that were skipped (with the reason — usually
   *  requiresRoomKind unmet). Surfaced honestly to the UI. */
  readonly skipped: ReadonlyArray<{ readonly stageId: string; readonly reason: string }>;
}

interface ProjectStage {
  readonly id: string;
  readonly label: string;
  readonly offsetFromHandoverDays: number;
  readonly slackDays: number;
  readonly citation: string;
  readonly dependsOnStageId: string | null;
  readonly requiresRoomKind: string | null;
  readonly note?: string;
}

const STAGES: ReadonlyArray<ProjectStage> = (codegen.projectStages ?? []) as ReadonlyArray<ProjectStage>;

function attrStr(n: GraphNode, key: string): string | null {
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

/** Resolve the project handover date.
 *  Priority:
 *    1. Project node attr `handoverDate` (ISO).
 *    2. Project node attr `handoverIso`.
 *    3. Latest existing :Milestone with status NOT done (if any).
 *    4. Fallback: today + the negative offset of the latest stage as days.
 *  Never silently invents a date — returns null if no signal at all. */
function resolveHandoverDate(graph: ProjectGraph): Date | null {
  const project = graph.nodes.find((n) => n.kind === "Project");
  if (project) {
    const h1 = attrStr(project, "handoverDate") ?? attrStr(project, "handoverIso");
    if (h1) {
      const d = new Date(h1);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  // Look for an existing "Handover" milestone — same stage id.
  const existing = graph.nodes.find((n) =>
    n.kind === "Milestone" && n.label.toLowerCase().includes("handover"));
  if (existing) {
    const due = attrStr(existing, "dueOn");
    if (due) {
      const d = new Date(due);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  return null;
}

function projectRoomKinds(graph: ProjectGraph): ReadonlySet<string> {
  const kinds = new Set<string>();
  for (const n of graph.nodes) {
    if (n.kind !== "Room") continue;
    const kind = attrStr(n, "roomKind");
    if (kind) kinds.add(kind.toLowerCase());
    // Also infer from the room label as a fallback so demo data with
    // empty roomKind doesn't suppress kitchen-install etc.
    if (n.label.toLowerCase().includes("kitchen")) kinds.add("kitchen");
    if (n.label.toLowerCase().includes("bath"))    kinds.add("bath");
  }
  return kinds;
}

function isoStartOfDay(d: Date): string {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.toISOString();
}

/** Synthesize a typed schedule for the given project graph. Idempotent —
 *  calling twice on the same graph (with the same nowIso) produces the
 *  same ids so persistence dedupes cleanly. */
export function synthesizeSchedule(graph: ProjectGraph, options?: {
  readonly nowIso?: string;
  /** Override the handover date; otherwise resolved from the graph. */
  readonly handoverDateIso?: string;
}): SynthResult {
  const now = options?.nowIso ?? new Date().toISOString();
  const projectId = graph.projectId;
  const handover = options?.handoverDateIso
    ? new Date(options.handoverDateIso)
    : resolveHandoverDate(graph);

  // No handover signal — emit an empty result rather than invent one.
  if (!handover || Number.isNaN(handover.getTime())) {
    const activity: GraphNode = {
      id: `synth-${projectId}-${now.replace(/[:.]/g, "-")}`,
      kind: "CatalogImport", // closest existing prov:Activity subclass we already render
      label: "Schedule synthesis — no handover date in project graph",
      attrs: { atTime: now, source: "agent/schedule-synth", datasetId: projectId, rowCount: 0, skippedRows: STAGES.length },
    };
    return { milestones: [], dependsOn: [], wasGeneratedBy: [], activity, skipped: STAGES.map((s) => ({ stageId: s.id, reason: "no handover date" })) };
  }

  const roomKinds = projectRoomKinds(graph);
  const activityId = `synth-${projectId}-${now.replace(/[:.]/g, "-")}`;
  const milestones: GraphNode[] = [];
  const dependsOn: GraphEdge[] = [];
  const wasGeneratedBy: GraphEdge[] = [];
  const skipped: { stageId: string; reason: string }[] = [];
  const emittedByStage = new Map<string, string>(); // stageId → milestone id

  for (const stage of STAGES) {
    if (stage.requiresRoomKind !== null) {
      if (!roomKinds.has(stage.requiresRoomKind.toLowerCase())) {
        skipped.push({ stageId: stage.id, reason: `no Room with roomKind '${stage.requiresRoomKind}' in project graph` });
        continue;
      }
    }
    const due = new Date(handover);
    due.setDate(handover.getDate() + stage.offsetFromHandoverDays);
    const milestoneId = `${projectId}-ms-${stage.id}`;
    milestones.push({
      id: milestoneId,
      kind: "Milestone",
      label: stage.label,
      attrs: {
        dueOn: isoStartOfDay(due),
        slackDays: stage.slackDays,
        status: due.getTime() < Date.now() ? "done" : "todo",
        citation: stage.citation,
        stageId: stage.id,
        synthesisActivityId: activityId,
        ...(stage.note !== undefined ? { note: stage.note } : {}),
      },
    });
    emittedByStage.set(stage.id, milestoneId);
    wasGeneratedBy.push({
      id: `edge-wgb-${milestoneId}-${activityId}`,
      kind: "WAS_GENERATED_BY",
      source: milestoneId,
      target: activityId,
      weight: 1,
      attrs: { atTime: now },
    });
    if (stage.dependsOnStageId) {
      const predId = emittedByStage.get(stage.dependsOnStageId);
      if (predId) {
        dependsOn.push({
          id: `edge-dep-${predId}-${milestoneId}`,
          kind: "DEPENDS_ON",
          source: milestoneId,
          target: predId,
          weight: 1,
          attrs: { stageId: stage.id, predecessorStageId: stage.dependsOnStageId },
        });
      }
    }
  }

  const activity: GraphNode = {
    id: activityId,
    kind: "CatalogImport", // PROV-O Activity surface; a dedicated :Synthesis kind ships when generated types catch up
    label: `Schedule synthesized — ${milestones.length} milestone${milestones.length === 1 ? "" : "s"} from project graph`,
    attrs: {
      atTime: now,
      source: "agent/schedule-synth",
      datasetId: projectId,
      rowCount: milestones.length,
      skippedRows: skipped.length,
    },
  };

  return { milestones, dependsOn, wasGeneratedBy, activity, skipped };
}

// ─── Persistence (localStorage write-through cache) ─────────────────────

const SYNTH_KEY_PREFIX = "foyrmind.scheduleSynth.";

interface PersistedSynth {
  readonly activity: GraphNode;
  readonly milestones: ReadonlyArray<GraphNode>;
  readonly dependsOn: ReadonlyArray<GraphEdge>;
  readonly wasGeneratedBy: ReadonlyArray<GraphEdge>;
}

export function readSynthSchedule(projectId: string): PersistedSynth | null {
  try {
    const raw = window.localStorage.getItem(SYNTH_KEY_PREFIX + projectId);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedSynth;
  } catch (err) {
    console.error("[scheduleSynth] failed to read", err);
    return null;
  }
}

export function writeSynthSchedule(projectId: string, result: SynthResult): void {
  try {
    const payload: PersistedSynth = {
      activity: result.activity,
      milestones: result.milestones,
      dependsOn: result.dependsOn,
      wasGeneratedBy: result.wasGeneratedBy,
    };
    window.localStorage.setItem(SYNTH_KEY_PREFIX + projectId, JSON.stringify(payload));
  } catch (err) {
    console.error("[scheduleSynth] failed to persist", err);
  }
}

export function clearSynthSchedule(projectId: string): void {
  try { window.localStorage.removeItem(SYNTH_KEY_PREFIX + projectId); }
  catch (err) { console.error("[scheduleSynth] failed to clear", err); }
}
